/**
 * WebSocket connection status indicator.
 *
 * Displays a banner when the WebSocket connection is lost,
 * with a reconnecting message and spinner.
 */
import React from 'react';
import {
    Box,
    Flex,
    Text,
    Spinner,
    useColorModeValue,
    Collapse,
} from '@chakra-ui/react';
import { useWebSocket } from '../../../context/WebSocketContext';
import { useAuth } from '../../../context/AuthContext';

/**
 * WebSocket Status Banner Component.
 *
 * Shows a subtle banner at the bottom of the page when WebSocket
 * is disconnected or reconnecting. Only shown for authenticated users.
 *
 * States:
 * - Reconnecting (orange + spinner): reconnection-in-progress message
 * - Max attempts reached (red): lastError message
 * - Disconnected (orange): real-time connection lost message
 */
const WebSocketStatus = (): JSX.Element => {
    const { connectionState, isConnected, isReconnecting, lastError, WS_STATES } = useWebSocket();
    const { user } = useAuth();

    // Only show when authenticated AND (disconnected or reconnecting)
    // Don't show if user is not logged in (WebSocket is intentionally disconnected)
    const shouldShow = !!user && !isConnected && connectionState !== WS_STATES.CONNECTING;

    // Determine state: reconnecting takes priority over lastError
    // lastError is only shown when we've given up (not reconnecting anymore)
    const isFatalError = !!lastError && !isReconnecting;

    // Use red for fatal errors, orange for reconnecting/disconnected
    const bgColor = useColorModeValue(
        isFatalError ? 'red.100' : 'orange.100',
        isFatalError ? 'red.800' : 'orange.800'
    );
    const textColor = useColorModeValue(
        isFatalError ? 'red.800' : 'orange.800',
        isFatalError ? 'red.100' : 'orange.100'
    );

    // Message priority: reconnecting > fatal error > generic disconnect
    const getMessage = (): string => {
        if (isReconnecting) return 'Reconnexion en cours...';
        if (lastError) return lastError;
        return 'Connexion temps-réel perdue';
    };

    return (
        <Collapse in={shouldShow} animateOpacity>
            <Box
                bg={bgColor}
                py={2}
                px={4}
                position="fixed"
                bottom={0}
                left={0}
                right={0}
                zIndex={1000}
            >
                <Flex align="center" justify="center" gap={2}>
                    {isReconnecting && (
                        <Spinner size="sm" color={textColor} />
                    )}
                    <Text fontSize="sm" color={textColor} fontWeight="medium">
                        {getMessage()}
                    </Text>
                </Flex>
            </Box>
        </Collapse>
    );
};

export default WebSocketStatus;